import React, { useCallback, useEffect, useRef } from 'react';

import Avatar from 'common-ui/chat/Avatar';
import { formatChatListUnreadCount } from 'common-ui/chat/constants';
import { useInstagramConversationsInfiniteScroll } from 'hooks/useChatInfiniteScroll';
import { formatChatListTime } from 'utils/dateUtils';

import {
  ChatListBlock,
  ChatListItem,
  ChatListItemBlock,
  ChatListItemLeftSide,
  ChatListItemRightSide,
  ChatListItemText,
  ChatListItemTime,
  ChatListItemTitle,
  ChatListItemTitleBlock,
  UnreadBadge,
} from './ChatList.styles';

const BUSINESS_ACCOUNT_ID = '176902635512677';

const InstagramChatList = ({ onConversationSelect, limit = 10 }) => {
  const { conversations, loading, hasNextPage, loadMore } =
    useInstagramConversationsInfiniteScroll(limit);

  const scrollContainerRef = useRef(null);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const options = {
      root: scrollContainerRef.current,
      rootMargin: '50px',
      threshold: 0.1,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !loading) {
        loadMore();
      }
    }, options);

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasNextPage, loading, loadMore]);

  const getLastMessageText = useCallback((latestMessage) => {
    if (!latestMessage) return 'No messages yet';

    if (latestMessage.message && latestMessage.message.trim()) {
      return latestMessage.message;
    }

    if (latestMessage.attachments?.length > 0) {
      const attachment = latestMessage.attachments[0];
      if (attachment.type === 'image') return '[Image]';
      if (attachment.type === 'video') return '[Video]';
      if (attachment.type === 'file') return '[File]';
      return '[Attachment]';
    }

    return 'No messages yet';
  }, []);

  const transformConversation = useCallback(
    (igConversation) => {
      const customerParticipant =
        igConversation?.participants?.data?.find((p) => p.id !== BUSINESS_ACCOUNT_ID) || {};

      const latestMessage = igConversation?.messages?.data?.[0];
      return {
        conversationId: customerParticipant.id || igConversation.id,
        sender: {
          id: customerParticipant.id,
          name:
            customerParticipant.name ||
            `User ${customerParticipant.id?.slice(-6) || 'Unknown'}`,
          username: customerParticipant.username,
          avatar: null,
        },
        lastMessage: getLastMessageText(latestMessage),
        lastMessageAt: igConversation.updated_time || latestMessage?.created_time,
        unreadCount: igConversation.unread_count || 0,
        platform: 'instagram',
        _raw: igConversation,
      };
    },
    [getLastMessageText]
  );

  if (loading && conversations.length === 0) {
    return (
      <ChatListBlock ref={scrollContainerRef}>
        <ChatListItem>
          <ChatListItemBlock>
            <ChatListItemText>Loading conversations...</ChatListItemText>
          </ChatListItemBlock>
        </ChatListItem>
      </ChatListBlock>
    );
  }

  if (conversations.length === 0) {
    return (
      <ChatListBlock ref={scrollContainerRef}>
        <ChatListItem>
          <ChatListItemBlock>
            <ChatListItemText>No conversations found</ChatListItemText>
          </ChatListItemBlock>
        </ChatListItem>
      </ChatListBlock>
    );
  }

  return (
    <ChatListBlock ref={scrollContainerRef}>
      {conversations.map((igConversation) => {
        const conversation = transformConversation(igConversation);
        const hasUnread = conversation.unreadCount > 0;

        return (
          <ChatListItem
            key={conversation.id}
            onClick={() => onConversationSelect?.(conversation)}
          >
            <ChatListItemBlock>
              <ChatListItemLeftSide>
                <Avatar
                  name={conversation.sender.name}
                  src={conversation.sender?.avatar}
                  platform="instagram"
                  size={36}
                />
                <ChatListItemTitleBlock>
                  <ChatListItemTitle>{conversation.sender.name}</ChatListItemTitle>
                  <ChatListItemText $hasUnread={hasUnread}>
                    {conversation.lastMessage.length > 100
                      ? `${conversation.lastMessage.substring(0, 100)}...`
                      : conversation.lastMessage}
                  </ChatListItemText>
                </ChatListItemTitleBlock>
              </ChatListItemLeftSide>
              <ChatListItemRightSide>
                {hasUnread && (
                  <UnreadBadge>
                    {formatChatListUnreadCount(conversation.unreadCount)}
                  </UnreadBadge>
                )}
                <ChatListItemTime>
                  {formatChatListTime(conversation.lastMessageAt)}
                </ChatListItemTime>
              </ChatListItemRightSide>
            </ChatListItemBlock>
          </ChatListItem>
        );
      })}

      {hasNextPage && (
        <div ref={sentinelRef} style={{ height: '1px', margin: '0' }}>
          {loading && (
            <ChatListItem>
              <ChatListItemBlock>
                <ChatListItemText style={{ textAlign: 'center', padding: '8px 0' }}>
                  Loading more...
                </ChatListItemText>
              </ChatListItemBlock>
            </ChatListItem>
          )}
        </div>
      )}
    </ChatListBlock>
  );
};

export default InstagramChatList;
